import { FC, useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import {
  ExperimentStatus,
  GetTasterExperimentSummaryResponse,
} from "api/client";

import { useWindowSize } from "hooks/window-size";

import ActivityFilled from "icons/ActivityFilled";

import { Button } from "components/common/Button";
import { Drawer } from "components/common/Drawer";
import { Tooltip } from "components/common/Tooltip";

import {
  StyledExperimentInfo,
  StyledExperimentInfoContent,
  StyledExperimentInfoField,
  StyledExperimentInfoFields,
  StyledExperimentInfoFooter,
  StyledExperimentInfoIcon,
} from "styles/features/home";
import {ConfirmationModal} from "../../../../components/common/modals/ConfirmationModal";

interface ExperimentInfoDrawerProps {
  experiment?: GetTasterExperimentSummaryResponse;
  open?: boolean;
  loading?: boolean;

  onClose?: () => void;
  onApprove?: (experimentId: string) => void;
  onReject?: (experimentId: string) => void;
}

const initialExperiment: GetTasterExperimentSummaryResponse = {
  id: "1",
  name: "Test experiment name",
  schedule: {
    startTime: "2024-03-07T00:00:00",
    location: "Test experiment location",
    room: "Test experiment room",
  },
  status: ExperimentStatus.DRAFT,
  isApproved: true,
};

const ExperimentInfoDrawer: FC<ExperimentInfoDrawerProps> = ({
  open,
  experiment = initialExperiment,
  loading,
  onClose,
  onApprove,
  onReject,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation
  const { windowSize } = useWindowSize();

  const { id, name, isApproved, schedule } = experiment;

  const { startTime, location, room } = schedule;

  const iconContainerClassName = clsx("experiment-info-icon", {
    blue: isApproved,
  });

  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);

  const renderedDate = useMemo(() => {
    if (startTime) {
      const date = new Date(startTime as string).toLocaleDateString();

      return date !== "Invalid Date" ? date : "-";
    }

    return "-";
  }, [startTime]);

  const renderedTime = useMemo(() => {
    if (startTime) {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "numeric",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(new Date(startTime as string));

      return time !== "Invalid Date" ? time : "-";
    }

    return "-";
  }, [startTime]);

  const renderedApproveBtn = useMemo(
    () =>
      !isApproved ? (
        <Button
          size="md"
          onClick={() => onApprove?.(id)}
          isLoading={loading}
          disabled={loading}
        >
          {t('approve')}
        </Button>
      ) : null,
    [id, isApproved, loading, onApprove, t]
  );

  const handleClose = useCallback(() => {
    if (loading) return;

    onClose?.();
  }, [loading, onClose]);

  return (
    <Drawer
      open={open}
      width={windowSize.width < 768 ? "100%" : "564px"}
      onClose={handleClose}
    >
      {open ? (
        <StyledExperimentInfo>
          <StyledExperimentInfoContent>
            <StyledExperimentInfoIcon className={iconContainerClassName}>
              <ActivityFilled
                color={
                  isApproved
                    ? theme.palette.blue.main
                    : theme.palette.yellow.main
                }
              />
            </StyledExperimentInfoIcon>
            <Typography variant="bodyM" color={theme.palette.customGrey.dark}>
              {t('experiment')}
            </Typography>
            <Typography
              variant="h2"
              color={theme.palette.common.black}
              marginBottom="32px"
            >
              {name}
            </Typography>
            <StyledExperimentInfoFields>
              <StyledExperimentInfoField>
                <Typography
                  variant="bodyM"
                  fontWeight={300}
                  color={theme.palette.customGrey.dark}
                >
                  {t('location')}
                </Typography>
                <Tooltip
                  text={location && location.length > 30 ? location : undefined}
                  size="auto"
                  wrapClassName="field-tooltip"
                  position="bottom-right"
                >
                  <Typography
                    variant="bodyM"
                    fontWeight={500}
                    color={theme.palette.common.black}
                    maxWidth="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {location || "-"}
                  </Typography>
                </Tooltip>
              </StyledExperimentInfoField>
              <StyledExperimentInfoField>
                <Typography
                  variant="bodyM"
                  fontWeight={300}
                  color={theme.palette.customGrey.dark}
                >
                  {t('date')}
                </Typography>
                <Typography
                  variant="bodyM"
                  fontWeight={500}
                  color={theme.palette.common.black}
                  maxWidth="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {renderedDate || "-"}
                </Typography>
              </StyledExperimentInfoField>
              <StyledExperimentInfoField>
                <Typography
                  variant="bodyM"
                  fontWeight={300}
                  color={theme.palette.customGrey.dark}
                >
                  {t('time')}
                </Typography>
                <Typography
                  variant="bodyM"
                  fontWeight={500}
                  color={theme.palette.common.black}
                  maxWidth="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {renderedTime || "-"}
                </Typography>
              </StyledExperimentInfoField>
              <StyledExperimentInfoField>
                <Typography
                  variant="bodyM"
                  fontWeight={300}
                  color={theme.palette.customGrey.dark}
                >
                  {t('room')}
                </Typography>
                <Tooltip
                  text={room && room.length > 30 ? room : undefined}
                  size="auto"
                  wrapClassName="field-tooltip"
                  position="bottom-right"
                >
                  <Typography
                    variant="bodyM"
                    fontWeight={500}
                    color={theme.palette.common.black}
                    maxWidth="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {room || "-"}
                  </Typography>
                </Tooltip>
              </StyledExperimentInfoField>
            </StyledExperimentInfoFields>
          </StyledExperimentInfoContent>
          <StyledExperimentInfoFooter>
            {renderedApproveBtn}
            <Button
              size="md"
              onClick={() => setIsRejectModalOpen(true)}
              variant={isApproved ? "primary" : "secondary"}
              isLoading={loading}
              disabled={loading}
            >
              {t('reject')}
            </Button>
          </StyledExperimentInfoFooter>
          <ConfirmationModal
              type="warning"
              open={isRejectModalOpen}
              onClose={() => setIsRejectModalOpen(false)}
              title={t('reject_confirm_title')}
              text={t('reject_confirm_text')}
              submitBtnText={t('reject')}
              cancelBtnText={t('cancel')}
              onCancel={() => setIsRejectModalOpen(false)}
              onSubmit={() => {
                onReject?.(id);
                setIsRejectModalOpen(false);
              }}>
          </ConfirmationModal>
        </StyledExperimentInfo>
      ) : null}
    </Drawer>
  );
};

export default ExperimentInfoDrawer;
